import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, MapPin, LogOut, MessageCircle, Plus, History, Store, Languages, ChevronDown, Package2, X as XIcon, Edit3, Phone as PhoneIcon, Trash2, Check, Heart, Gift, Calendar, Truck, Wallet as WalletIcon, ChevronRight, Receipt } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT, LANGUAGES } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import RoleSwitcher from "../../components/RoleSwitcher";
import { toast } from "sonner";
import LoginRequired from "./LoginRequired";
import AddressDialog from "../../components/AddressDialog";
import LegalFooter from "../../components/LegalFooter";
import { useAppVersion, useNotificationPermission } from "../../lib/deviceInfo";
import { splitPhone } from "../../lib/phone";
import { deletionBlockedReason } from "../../lib/accountDeletion";

export default function Profile() {
  const { user, tenant, logout, refresh } = useAuth();
  const { t, lang, setLang } = useT();
  const [addresses, setAddresses] = useState([]);
  const [bottles, setBottles] = useState({ bottles_held: 0, open_pickup_request: null });
  const [addrOpen, setAddrOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupCount, setPickupCount] = useState(1);
  const [pickupNotes, setPickupNotes] = useState("");
  const [busy, setBusy] = useState(false);
  // Apple-mandated account deletion (5.1.1(v)). State lives at the top so
  // the dialog can be rendered next to the existing dialogs at the end
  // of the component.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  // The wallet balance, read when the delete dialog opens: a negative
  // balance blocks deletion (the server refuses it as well).
  const [deleteBlocked, setDeleteBlocked] = useState(null);
  useEffect(() => {
    if (!deleteOpen) { setDeleteBlocked(null); return; }
    let live = true;
    api.get("/api/customer/wallet")
      .then(({ data }) => { if (live) setDeleteBlocked(deletionBlockedReason(data?.wallet?.balance)); })
      .catch(() => {});                          // the server check still applies
    return () => { live = false; };
  }, [deleteOpen]);

  const loadBottles = () => api.get("/api/customer/me/bottles").then(({ data }) => setBottles(data)).catch(() => {});
  const loadAddresses = () => api.get("/api/customer/addresses").then(({ data }) => setAddresses(data.addresses)).catch(() => {});

  // When the tenant has turned bottle tracking off in Admin → Settings →
  // Operations, every bottle UI on the customer side disappears (and we
  // skip the network call entirely).
  const bottlesOn = tenant?.bottle_tracking_enabled !== false;

  // Refer & Earn opens whenever the backend serves /api/customer/referrals/me
  // — the referral module OR the tenant's "Referral code at signup" setting —
  // so customers can always reach the code the signup screen asks for.
  // `referral_page_available` is computed server-side (routes_auth →
  // routes_referrals.referral_page_available); the module check is only the
  // fallback for a tenant payload that predates the flag.
  const tenantModules = tenant?.effective_modules || tenant?.enabled_modules || [];
  const referralsOn = typeof tenant?.referral_page_available === "boolean"
    ? tenant.referral_page_available
    : tenantModules.includes("referral");

  useEffect(() => {
    if (user) {
      loadAddresses();
      if (bottlesOn) loadBottles();
    }
  }, [user, bottlesOn]);

  if (!user) return <LoginRequired title="Sign in to view your profile" description="Manage addresses, language and account settings." />;

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name) { toast.error("Name cannot be empty"); return; }
    setBusy(true);
    try {
      await api.patch("/api/customer/me", { name });
      toast.success("Name updated");
      setNameOpen(false);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/api/customer/addresses/${id}`);
    toast.success("Address removed");
    loadAddresses();
  };

  const submitPickup = async () => {
    if (pickupCount <= 0) { toast.error("Add at least 1 bottle"); return; }
    setBusy(true);
    try {
      await api.post("/api/customer/me/bottles/request-pickup", { count: pickupCount, notes: pickupNotes });
      toast.success("Pickup requested! Our team will collect on next delivery.");
      setPickupOpen(false); setPickupNotes(""); setPickupCount(1);
      loadBottles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to request pickup");
    } finally { setBusy(false); }
  };

  const cancelPickup = async () => {
    if (!bottles.open_pickup_request) return;
    await api.delete(`/api/customer/me/bottles/request-pickup/${bottles.open_pickup_request.id}`);
    toast.success("Pickup request cancelled");
    loadBottles();
  };

  return (
    <div className="page-enter" data-testid="profile-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/home" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">{t("me")}</h1>
        </div>
      </header>

      <main className="px-5 space-y-4">
        <div className="bg-white rounded-3xl border border-neutral-200 p-5 flex items-center gap-4" data-testid="user-card">
          <div className="w-14 h-14 rounded-2xl bg-brand-sky/10 text-brand-sky flex items-center justify-center font-heading font-extrabold text-xl">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-heading font-bold text-lg truncate">{user?.name || "Add your name"}</div>
              <button onClick={() => { setNameDraft(user?.name || ""); setNameOpen(true); }}
                      className="w-7 h-7 rounded-full bg-neutral-100 text-neutral-600 inline-flex items-center justify-center" data-testid="edit-name-btn"
                      aria-label="Edit name">
                <Edit3 className="w-3.5 h-3.5"/>
              </button>
            </div>
            {(() => {
              const { cc, local } = splitPhone(user?.phone);
              return (
                <div className="text-sm text-neutral-500 inline-flex items-center gap-1.5 mt-0.5" data-testid="user-phone">
                  <PhoneIcon className="w-3.5 h-3.5"/>
                  <span className="font-mono font-bold text-neutral-700">{cc}</span>
                  <span className="font-mono">{local && local.length === 10 ? `${local.slice(0,5)} ${local.slice(5)}` : local}</span>
                </div>
              );
            })()}
            <div className="text-xs text-brand-sky font-bold mt-0.5">{tenant?.name}</div>
          </div>
        </div>

        {/* Language Switcher */}
        <div className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="language-card">
          <div className="flex items-center gap-3 mb-3">
            <Languages className="w-5 h-5 text-brand-sky" />
            <div className="font-heading font-bold">{t("language")}</div>
          </div>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-12 rounded-2xl" data-testid="language-select">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-${l.code}`}>
                  <span className="font-bold">{l.native}</span>
                  {l.label !== l.native && <span className="text-neutral-500 ml-2 text-xs">{l.label}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Customer menu — primary navigation lives here. */}
        <div className="bg-white rounded-3xl border border-neutral-200 divide-y divide-neutral-100 overflow-hidden" data-testid="profile-menu">
          <ProfileMenuLink to="/app/next-delivery" icon={Truck} label="My Next Delivery" sub="See what's coming next" testid="menu-next-delivery"/>
          <ProfileMenuLink to="/app/orders" icon={Package2} label="My Orders" sub="One-time orders & history" testid="menu-orders"/>
          <ProfileMenuLink to="/app/subscriptions" icon={Calendar} label="My Subscriptions" sub="Pause, edit or cancel" testid="menu-subscriptions"/>
          <ProfileMenuLink to="/app/addresses" icon={MapPin} label="Manage Addresses" sub={`${addresses.length} saved`} testid="menu-addresses"/>
          <ProfileMenuLink to="/app/wallet" icon={WalletIcon} label="Wallet" sub="Balance & recharge" testid="menu-wallet"/>
          <ProfileMenuLink to="/app/bills" icon={Receipt} label="My Bills" sub="Weekly/monthly invoices" testid="menu-bills"/>
        </div>

        {/* Loyalty + Referrals quick links — Loyalty when its addon is active; Referrals whenever the referral page is served (see referralsOn) */}
        {(tenant?.effective_modules || tenant?.enabled_modules || []).includes("loyalty") && (
          <Link to="/app/loyalty" className="bg-white rounded-3xl border border-neutral-200 p-5 flex items-center gap-3 card-hover" data-testid="loyalty-link">
            <Heart className="w-5 h-5 text-brand-clay"/>
            <div className="flex-1">
              <div className="font-bold">Loyalty points</div>
              <div className="text-xs text-neutral-500">Earn points on every delivery · redeem to wallet</div>
            </div>
            <ChevronDown className="w-4 h-4 -rotate-90 text-neutral-400"/>
          </Link>
        )}
        {referralsOn && (
          <Link to="/app/referrals" className="bg-white rounded-3xl border border-neutral-200 p-5 flex items-center gap-3 card-hover" data-testid="referrals-link">
            <Gift className="w-5 h-5 text-brand-sky"/>
            <div className="flex-1">
              <div className="font-bold">{tenantModules.includes("referral") ? "Refer & Earn" : "Refer a friend"}</div>
              <div className="text-xs text-neutral-500">
                {/* Rewards need the referral module; a signup-code-only store
                    hands out codes without paying, so don't promise credit. */}
                {tenantModules.includes("referral") ? "Share your code · earn wallet credit" : "Share your code with friends"}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 -rotate-90 text-neutral-400"/>
          </Link>
        )}

        {/* Bottles with you card — only when the tenant has bottle tracking on */}
        {bottlesOn && (
        <div className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="bottles-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-2xl bg-brand-sky/10 text-brand-sky flex items-center justify-center">
              <Package2 className="w-5 h-5" strokeWidth={2.4}/>
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold">Bottles with you</div>
              <div className="text-xs text-neutral-500">Help us collect empties on your next delivery</div>
            </div>
            <div className="font-heading text-3xl font-extrabold text-brand-sky">🍶 {bottles.bottles_held}</div>
          </div>
          {bottles.open_pickup_request ? (
            <div className="rounded-2xl bg-brand-leaf/10 border border-brand-leaf/30 p-3 flex items-center gap-3" data-testid="open-pickup">
              <div className="flex-1">
                <div className="font-bold text-sm text-brand-leaf">Pickup requested ✓</div>
                <div className="text-xs text-neutral-600">{bottles.open_pickup_request.count} bottles · Will be collected on next visit</div>
              </div>
              <button onClick={cancelPickup} className="w-9 h-9 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-neutral-500" data-testid="cancel-pickup-btn">
                <XIcon className="w-4 h-4"/>
              </button>
            </div>
          ) : bottles.bottles_held > 0 ? (
            <Button onClick={() => { setPickupCount(bottles.bottles_held); setPickupOpen(true); }}
                    className="w-full h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold"
                    data-testid="request-pickup-btn">
              Request bottle pickup
            </Button>
          ) : (
            <div className="text-sm text-neutral-500 text-center py-1">No empty bottles to return right now</div>
          )}
        </div>
        )}

        {/* Start Store CTA removed — drive new tenants via /startstore URL only */}

        {tenant?.branding?.whatsapp && (
          <a href={`https://wa.me/${tenant.branding.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
             className="bg-brand-leaf/10 border border-brand-leaf/30 rounded-3xl p-5 flex items-center gap-3"
             data-testid="whatsapp-link">
            <MessageCircle className="w-5 h-5 text-brand-leaf" />
            <span className="font-bold">{t("whatsapp_support")}</span>
          </a>
        )}

        <RoleSwitcher />

        <button onClick={logout} className="w-full bg-white rounded-3xl border border-neutral-200 p-5 flex items-center gap-3 text-brand-clay font-bold" data-testid="logout-btn">
          <LogOut className="w-5 h-5" /> {t("logout")}
        </button>

        {/* Account deletion — required by Apple App Store guideline 5.1.1(v).
            Triggers a soft-delete on the backend: PII is anonymized, business
            records (orders / deliveries / payments) are retained for audit. */}
        <button
          onClick={() => setDeleteOpen(true)}
          className="w-full bg-white rounded-3xl border border-brand-clay/30 p-5 flex items-center gap-3 text-brand-clay font-bold"
          data-testid="delete-account-btn"
        >
          <Trash2 className="w-5 h-5" /> Delete my account
        </button>

        <div className="text-center text-xs text-neutral-400 pt-4 pb-2">{tenant?.name || "Your dairy"} · Made for Bharat 🇮🇳</div>
      </main>

      <Dialog open={pickupOpen} onOpenChange={setPickupOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="pickup-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Request bottle pickup</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-600 -mt-1">Our delivery boy will collect them on your next visit.</p>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-neutral-700">How many bottles?</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPickupCount(Math.max(1, pickupCount - 1))} className="w-10 h-10 rounded-full bg-brand-cream border border-neutral-200">−</button>
              <span className="font-mono font-extrabold text-xl w-8 text-center" data-testid="pickup-count-value">{pickupCount}</span>
              <button onClick={() => setPickupCount(Math.min(bottles.bottles_held, pickupCount + 1))} className="w-10 h-10 rounded-full bg-brand-cream border border-neutral-200">+</button>
            </div>
          </div>
          <Input placeholder="Notes (optional)" value={pickupNotes} onChange={(e) => setPickupNotes(e.target.value)} className="h-12 rounded-2xl" data-testid="pickup-notes"/>
          <Button onClick={submitPickup} disabled={busy} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="confirm-pickup-btn">
            {busy ? "Requesting…" : `Confirm pickup of ${pickupCount} bottle${pickupCount > 1 ? "s" : ""}`}
          </Button>
        </DialogContent>
      </Dialog>

      <AddressDialog open={addrOpen} onOpenChange={setAddrOpen} onSaved={() => loadAddresses()} />

      <Dialog open={nameOpen} onOpenChange={setNameOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="edit-name-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Your name</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-600 -mt-1">This is what we&rsquo;ll greet you with and what your delivery boy will see.</p>
          <Input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} placeholder="Full name"
                 className="h-12 rounded-2xl" autoFocus data-testid="name-input"/>
          <Button onClick={saveName} disabled={busy || !nameDraft.trim()} className="w-full h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="save-name-btn">
            <Check className="w-4 h-4 mr-2"/> Save
          </Button>
        </DialogContent>
      </Dialog>

      {/* Delete-my-account dialog (Apple/Google compliance). Backend does the
          soft-delete + anonymization; we explicitly call logout() afterwards
          so the auth context drops the now-disabled token. */}
      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(""); }}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-clay">Delete your account?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-neutral-700">
            <p>This will permanently sign you out and remove your personal data — name, phone, email, addresses, push tokens, cart and wishlist.</p>
            <p>For tax &amp; billing compliance we keep your past orders, deliveries, payments and invoices — they&rsquo;ll show as <b>Deleted User</b>.</p>
            <p>All your active subscriptions will be cancelled. <b>This action can&rsquo;t be undone.</b></p>
            {deleteBlocked && (
              <div className="rounded-2xl bg-brand-clay/10 text-brand-clay px-3 py-2 font-bold" data-testid="delete-wallet-not-zero">
                {deleteBlocked}{" "}
                <Link to="/app/wallet" className="underline" data-testid="delete-go-to-wallet">Open wallet</Link>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Type <b>DELETE</b> to confirm</label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="h-12 rounded-2xl font-mono mt-2"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          <Button
            onClick={async () => {
              if (deleteConfirm !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
              setDeleteBusy(true);
              try {
                await api.delete("/api/customer/me/account");
                toast.success("Account deleted. Signing you out…");
                // Small delay so the toast actually renders before the
                // auth context yanks us back to the login screen.
                setTimeout(() => logout(), 800);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Couldn't delete account");
              } finally {
                setDeleteBusy(false);
              }
            }}
            disabled={deleteBusy || deleteConfirm !== "DELETE" || !!deleteBlocked}
            className="w-full h-12 rounded-2xl bg-brand-clay hover:bg-orange-700 font-bold"
            data-testid="confirm-delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> {deleteBusy ? "Deleting…" : "Yes, delete my account"}
          </Button>
        </DialogContent>
      </Dialog>
      <LegalFooter theme="light"/>
      <DeviceInfoCard/>
    </div>
  );
}


/**
 * Compact diagnostic strip shown at the very bottom of the Profile page.
 *
 *   v 1.2.3 (build 18) · Notifications: Enabled
 *
 * Renders the *native* APK/IPA version on a real device (via @capacitor/app
 * App.getInfo) and the web build version on the browser, plus the current
 * OS-level notification permission state. Lets users and QA confirm at a
 * glance which build is installed and whether push permission is granted —
 * crucial when triaging "I'm not getting notifications" reports.
 */
function DeviceInfoCard() {
  const ver = useAppVersion();
  const perm = useNotificationPermission();

  const permLabel =
    perm.status === "enabled" ? "Enabled" :
    perm.status === "disabled" ? "Disabled" :
    perm.status === "prompt" ? "Not requested" :
    perm.status === "loading" ? "…" :
    "Unsupported";

  const permClass =
    perm.status === "enabled" ? "text-emerald-600" :
    perm.status === "disabled" ? "text-rose-600" :
    "text-neutral-500";

  return (
    <div className="px-4 pb-6 pt-2 text-center text-[11px] text-neutral-400" data-testid="device-info-card">
      <div className="font-mono" data-testid="device-info-version">
        v{ver.version}{ver.build ? ` (build ${ver.build})` : ""}
        {ver.source === "web" && <span className="text-neutral-300"> · web</span>}
      </div>
      <div data-testid="device-info-permission">
        Notifications: <span className={`font-semibold ${permClass}`} data-testid="device-info-permission-status">{permLabel}</span>
      </div>
    </div>
  );
}


function ProfileMenuLink({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link to={to} className="flex items-center gap-3 px-4 sm:px-5 py-4 active:bg-neutral-50 transition-colors" data-testid={testid}>
      <div className="w-10 h-10 rounded-2xl bg-brand-cream flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-brand-ink" strokeWidth={2.2}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm sm:text-base">{label}</div>
        {sub && <div className="text-xs text-neutral-500 truncate">{sub}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0"/>
    </Link>
  );
}
